import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'components/Form';
import Message from 'components/Message';
import { PERMISSION } from 'constants/logic';
import { schedulePropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { selectActiveVenue, selectUserHasPermission } from 'selectors/general';

import SegmentedCapacityBar, { SEGMENTED_CAPACITY_BAR_SEGMENT } from '../Components/SegmentedCapacityBar';
import { SCHEDULE_LIST_V2_FILL_BAR_VARIANT, getScheduleListV2FillBar } from '../ScheduleListV2/ScheduleListV2.helpers';
import { actOnOpportunityFromSchedule, addSpot } from '../YieldManagement.saga';
import { ADD_SPOT_ACTION, getAddSpotAction, getMostRecentFillHistoryEntry } from './ScheduleDetailV2.helpers';
import { useScheduleInsights } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';
import './AllBookingsSection.scss';

const { block, element } = bem('all-bookings-section');

const AllBookingsSection = ({ onViewInsights, schedule }) => {
  const dispatch = useDispatch();
  const { venueId } = useSelector(selectActiveVenue);
  const hasSpotManagePermission = useSelector(state =>
    selectUserHasPermission(state, PERMISSION.DISPLAY_SPOT_MANAGEMENT)
  );
  const [opportunityActedOn, setOpportunityActedOn] = useState(false);
  const { data: insights } = useScheduleInsights(schedule.scheduleId);
  const fillBar = getScheduleListV2FillBar(schedule);
  const isIntegrated = fillBar.variant === SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED;
  const unfilledCount = fillBar.segments.find(({ key }) => key === SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED)?.value ?? 0;
  const lastWeekFill = getMostRecentFillHistoryEntry(insights?.history, schedule.startDateTime);

  const addSpotAction = getAddSpotAction(schedule, { hasSpotManagePermission, opportunityActedOn });
  const showDemand = (schedule.spots?.classpassDemand ?? 0) > 0 && Boolean(addSpotAction);

  const handleAddSpot = () => {
    const classpassTotal = schedule.spots?.classpassTotal ?? 0;
    dispatch(addSpot(classpassTotal, schedule.scheduleId, classpassTotal + 1, schedule.smartspotEnabled, showDemand));
  };

  const handleActOnOpportunity = () => {
    dispatch(
      actOnOpportunityFromSchedule({
        className: schedule.scheduleName,
        opportunities: [schedule.opportunity],
        schedule,
        showDemand,
        source: 'schedule detail',
        spotsAdded: addSpotAction.recommendation,
        startDateTime: schedule.startDateTime,
        venueId,
      })
    );
    setOpportunityActedOn(true);
  };

  const legendCount = count => <span className={element('legend-count')}>{count}</span>;
  const legendItems = [
    isIntegrated && {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.DIRECT,
      label: <Message message={messages.legendDirect} values={{ count: legendCount(fillBar.directBooked) }} />,
    },
    {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.CLASSPASS,
      label: <Message message={messages.legendClasspass} values={{ count: legendCount(fillBar.classpassBooked) }} />,
    },
    {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED,
      label: <Message message={messages.legendOpenSpots} values={{ count: legendCount(unfilledCount) }} />,
    },
  ].filter(Boolean);

  return (
    <section className={block()} data-cypress="schedule-detail-v2-all-bookings">
      <div className={element('title-row')}>
        <Message
          as="h3"
          className={element('title')}
          message={isIntegrated ? messages.allBookingsTitleIntegrated : messages.allBookingsTitleManual}
        />
        {lastWeekFill && (
          <Message
            as="span"
            className={element('caption')}
            data-cypress="schedule-detail-v2-last-week-fill"
            message={messages.lastWeekFill}
            values={{ capacity: lastWeekFill.maxCapacity, filled: lastWeekFill.totalReservations }}
          />
        )}
      </div>
      <SegmentedCapacityBar legendItems={legendItems} muted={schedule.isInThePast} segments={fillBar.segments} />
      <div className={element('footer')}>
        {addSpotAction && (
          <Button
            className={element('add-spot')}
            styled="inline-link"
            data-cypress="schedule-detail-v2-add-spot"
            onClick={addSpotAction.type === ADD_SPOT_ACTION.OPPORTUNITY ? handleActOnOpportunity : handleAddSpot}>
            {addSpotAction.type === ADD_SPOT_ACTION.OPPORTUNITY ? (
              <Message message={messages.addNSpots} values={{ recommendation: addSpotAction.recommendation }} />
            ) : (
              <Message message={messages.addSpot} />
            )}
          </Button>
        )}
        <Button className={element('view-insights')} styled="inline-link" onClick={onViewInsights}>
          <Message message={messages.viewMoreInsights} />
        </Button>
      </div>
    </section>
  );
};

AllBookingsSection.propTypes = {
  onViewInsights: PropTypes.func,
  schedule: PropTypes.shape(schedulePropTypes),
};

export default AllBookingsSection;
